import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { X } from '@phosphor-icons/react';
import { updatePromptTemplate } from '@/services/promptTemplatesApi';
import { toast } from '@/state/toastStore';
import type { PromptTemplate } from '@/types';

interface Props {
  episodeUuid: string;
  template: PromptTemplate;
  onClose: () => void;
  onSaved: (next: PromptTemplate) => void;
}

const PLACEHOLDERS: { token: string; doc: string }[] = [
  { token: '{prompt}', doc: 'The user prompt for this scene.' },
  { token: '{style}', doc: 'Style preset description (or custom style).' },
  { token: '{cultural_guidelines}', doc: 'Filled when the toggle is on, empty otherwise.' },
  { token: '{aspect_ratio}', doc: 'e.g. 16:9.' },
  { token: '{cast}', doc: 'Joined character descriptions for active cast refs (image only).' },
  { token: '{previous_scenes}', doc: 'Short summary of referenced prior scenes (image only).' },
];

/**
 * Slide-over editor for an episode-scoped prompt template. The Style tab's
 * "Edit" button always lands here against a mutable row — system templates
 * are cloned first by the caller, never opened here directly.
 *
 * Save = PATCH on the row; the StyleTab refetches its template list so the
 * dropdown picks up the new name. The drawer also auto-grows the textarea.
 */
export function TemplateEditorDrawer({ episodeUuid, template, onClose, onSaved }: Props) {
  const [name, setName] = useState(template.name);
  const [body, setBody] = useState(template.body);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Reset local state when switching to a different template.
  useEffect(() => {
    setName(template.name);
    setBody(template.body);
  }, [template.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autogrow textarea up to a sensible max so long bodies don't push the
  // save button off-screen.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 480)}px`;
  }, [body]);

  // Esc closes the drawer.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    if (saving || template.isSystem) return;
    setSaving(true);
    try {
      const updated = await updatePromptTemplate(episodeUuid, template.id, {
        name: name.trim() || template.name,
        body,
      });
      toast.ok('Template saved.');
      onSaved(updated);
    } catch (err) {
      toast.err(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function insertPlaceholder(token: string) {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // Restore caret after the inserted token on next tick.
    requestAnimationFrame(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + token.length;
    });
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-label={`Edit template: ${template.name}`}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[640px] flex-col border-l border-line-1 bg-surface-1 shadow-elev-2"
      >
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line-1 px-4">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
              Edit · {template.kind}
            </span>
            {template.isSystem && (
              <span className="rounded-sm bg-warn/15 px-1.5 py-0.5 font-mono text-[10px] uppercase text-warn">
                read-only system
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-2 hover:text-text-1"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex flex-1 min-h-0 flex-col gap-4 overflow-y-auto p-4">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
              Name
            </span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={template.isSystem}
              className="w-full rounded-md bg-surface-inset px-3 py-2 text-[13px] text-text-1 placeholder:text-text-4 outline-none ring-1 ring-line-2 focus:ring-line-3 disabled:cursor-not-allowed disabled:text-text-4"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
              Body
            </span>
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={template.isSystem}
              rows={10}
              className="w-full resize-none rounded-md bg-surface-inset p-3 font-mono text-[12px] text-text-1 outline-none ring-1 ring-line-2 focus:ring-line-3 disabled:cursor-not-allowed disabled:text-text-4"
            />
          </label>

          <section>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-text-3">
              Placeholders (click to insert)
            </p>
            <ul className="space-y-1">
              {PLACEHOLDERS.map((p) => (
                <li key={p.token} className="flex items-baseline gap-2">
                  <button
                    type="button"
                    onClick={() => insertPlaceholder(p.token)}
                    disabled={template.isSystem}
                    className="rounded-sm bg-surface-3 px-1.5 py-0.5 font-mono text-[11px] text-accent transition-colors hover:bg-accent-soft disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {p.token}
                  </button>
                  <span className="text-[11px] text-text-3">{p.doc}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] text-text-4">
              Unknown <code className="font-mono">{'{tokens}'}</code> pass through unchanged.
              If you remove <code className="font-mono">{'{prompt}'}</code> the user prompt
              is appended automatically.
            </p>
          </section>
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-3 border-t border-line-1 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-[13px] font-medium text-text-3 transition-colors hover:bg-surface-2 hover:text-text-1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || template.isSystem}
            className={clsx(
              'rounded-md px-4 py-2 text-[13px] font-medium transition-colors',
              template.isSystem
                ? 'cursor-not-allowed bg-surface-3 text-text-4'
                : 'bg-accent text-text-on-accent hover:bg-accent-hover disabled:opacity-60',
            )}
          >
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </footer>
      </aside>
    </>
  );
}
